/**
 * bridge/contact_picker_multiple_controller.js — "contact-picker-multiple"
 * bridge component (Stimulus + Strada).
 *
 * Several contacts in one pick, for importing clients from the phone
 * (/clients/import). Registers window.ChippieBridge.contactPickerMultiple(),
 * which resolves to an ARRAY of { name, phone, email, address } (the same
 * shape the single contact-picker returns, via its normalize()), or rejects:
 *   - "contact-cancelled"   the user closed the picker without choosing
 *   - "contact-unavailable" this device has no multi-select picker; the
 *                           caller falls back to ChippieBridge.contactPicker()
 *
 * Per platform:
 *   - pwa:     the Contact Picker API with { multiple: true } (Chrome on
 *              Android, browser or installed PWA). Absent elsewhere.
 *   - ios:     ContactPickerMultipleComponent.swift, CNContactPickerViewController
 *              in multi-select mode (no contacts permission). Event "open",
 *              reply data { contacts: [...] }, an empty array on cancel.
 *   - android: not built. The system picker returns one contact per call and
 *              true multi-select needs READ_CONTACTS, which ships only with a
 *              feature that earns it (ADR-013). Rejects "contact-unavailable".
 *
 * Why a separate component rather than a new event on "contact-picker":
 * Hotwire Native lists each registered native component in the user agent,
 * and BridgeComponent only connects when its name is listed. An iOS build
 * from before this component never connects here, so the ios impl stays the
 * rejecting placeholder and callers fall back. A new event on the existing
 * component would get no reply from an old build and hang.
 *
 * Registration follows bridge/contact_picker_controller.js: the impls with no
 * per-instance state register at module load, the ios impl on connect().
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

import { flattenWebContact, normalize } from "./contact_picker_controller.js";

const unavailable = () => Promise.reject(new Error("contact-unavailable"));

async function pwaPickMany() {
  const picker = navigator.contacts;
  if (!picker || typeof picker.select !== "function") {
    throw new Error("contact-unavailable");
  }
  const props = ["name", "tel", "email", "address"];
  const supported =
    typeof picker.getProperties === "function" ? await picker.getProperties() : props;
  const results = await picker.select(
    props.filter((p) => supported.includes(p)),
    { multiple: true }
  );
  if (!results || results.length === 0) {
    throw new Error("contact-cancelled");
  }
  return results.map((c) => normalize(flattenWebContact(c)));
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("contactPickerMultiple", {
    ios: unavailable,
    android: unavailable,
    pwa: pwaPickMany,
  });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "contact-picker-multiple";

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("contactPickerMultiple", {
      ios: () =>
        new Promise((resolve, reject) => {
          this.send("open", {}, (message) => {
            const contacts = (message.data && message.data.contacts) || [];
            if (contacts.length === 0) {
              reject(new Error("contact-cancelled"));
            } else {
              resolve(contacts.map(normalize));
            }
          });
        }),
      android: unavailable,
      pwa: pwaPickMany,
    });
  }

  disconnect() {
    // Back to the rejecting placeholder rather than deleting the key, which
    // would also take the capability away from the PWA impl.
    registerStandalone();
    super.disconnect();
  }
}
